import { assoc, dissoc } from 'ramda'
import { API_STATES } from 'src/ducks/consts'
import { Messages, Conversation } from './records'
import {
  setMessagesListOpenedRoutine,
  setConversationOpenedRoutine,
  sendMessageRoutine,
  bootstrapConversationRoutine,
  getConversationMessagesRoutine,
  clearConversationRoutine,
  getLastContextualMessagesListRoutine,
  getLastPersonalMessagesListRoutine,
  getUnreadMessagesCountRoutine,
  searchForUserConversationsRoutine,
  clearLastMessagesListsRoutine,
  toggleConversationNotificationsRoutine,
  getConversationContextRoutine,
  getContextAvailableActionsRoutine,
  MESSAGE_EVENT_USER_MESSAGE_SENT,
  MESSAGE_EVENT_SYSTEM_MESSAGE_SENT,
  CHAT_PRESENCE_CHANNEL_LEAVE,
  CHAT_PRESENCE_CHANNEL_INIT,
  CHAT_PRESENCE_CHANNEL_JOIN,
  CHAT_OTHER_TAB_MESSAGE_SENT,
  CHAT_CONVERSATION_MESSAGES_READ,
  setChatSessionActiveRoutine,
  setChatSessionMinimizedRoutine,
  closeChatSessionRoutine,
  CHAT_SESSION_UI_UPDATED,
  setChatDockConstraintsRoutine,
  markMessageAsSeenRoutine
} from './actions'
import { normalizeReceivedMessage } from './normalizers'
import { CHAT_SESSION_STATUS } from './consts'
import {
  pushDistinctFront,
  removeFromList,
  sortDistinct,
  nowTimestamp,
  hasServerMessageId,
  isUnreadIncomingMessage,
  mergeMessageGroups
} from './helpers'

const toConversationRecord = session =>
  session instanceof Conversation ? session : new Conversation(session)

const updateChatSession = (state, chatId, updater) => {
  if (!chatId || !state.chatSessions?.[chatId]) return state
  const current = toConversationRecord(state.chatSessions?.[chatId])
  const prepared = updater(current)
  const updated = toConversationRecord(prepared)
  return state.update('chatSessions', sessions => ({
    ...(sessions || {}),
    [chatId]: updated
  }))
}

const mapConversationToChat = (state, conversationId, chatId) => {
  if (!conversationId || !chatId) return state
  return state.update('conversationChatMap', map => ({
    ...(map || {}),
    [String(conversationId)]: chatId
  }))
}

const pickActiveChatId = state => {
  const sessions = state.chatSessions || {}
  const exists = chatId =>
    chatId &&
    sessions?.[chatId] &&
    sessions[chatId].status === CHAT_SESSION_STATUS.OPEN

  if (exists(state.activeChatId)) {
    return state.activeChatId
  }

  const openCandidate = (state.openChatIds || []).find(exists)
  if (openCandidate) {
    return openCandidate
  }

  return null
}

const syncActiveConversation = state => {
  const activeChatId = pickActiveChatId(state)
  const sessions = state.chatSessions || {}
  const activeSession = activeChatId
    ? toConversationRecord(sessions?.[activeChatId])
    : new Conversation()

  return state
    .set('activeChatId', activeChatId)
    .set('conversation', activeSession)
    .set('conversationOpened', Boolean(activeChatId))
    .set(
      'conversationState',
      activeChatId ? activeSession.messagesState : API_STATES.PRISTINE
    )
}

const enforceDockLimits = (state, timestamp = nowTimestamp()) => {
  let nextState = state
  let openChatIds = [...(state.openChatIds || [])]
  let minimizedChatIds = [...(state.minimizedChatIds || [])]
  const { maxOpenChats, maxDockChats } = state

  while (openChatIds.length > maxOpenChats) {
    const movedChatId = openChatIds.pop()
    minimizedChatIds = pushDistinctFront(minimizedChatIds, movedChatId)
    nextState = updateChatSession(nextState, movedChatId, session =>
      session.merge({
        status: CHAT_SESSION_STATUS.MINIMIZED,
        minimizedAt: timestamp,
        lastActivityAt: timestamp
      })
    )
  }

  let combined = [...openChatIds, ...minimizedChatIds]
  while (combined.length > maxDockChats) {
    let removedChatId = null
    if (minimizedChatIds.length > 0) {
      removedChatId = minimizedChatIds.pop()
    } else if (openChatIds.length > 0) {
      removedChatId = openChatIds.pop()
    }

    if (!removedChatId) break

    nextState = updateChatSession(nextState, removedChatId, session =>
      session.merge({
        status: CHAT_SESSION_STATUS.CLOSED,
        minimizedAt: null
      })
    )

    combined = [...openChatIds, ...minimizedChatIds]
  }

  return nextState
    .set('openChatIds', openChatIds)
    .set('minimizedChatIds', minimizedChatIds)
}

const countUnreadMessages = groups =>
  groups.reduce(
    (count, group) => count + group.filter(isUnreadIncomingMessage).length,
    0
  )

const appendMessageToSession = (session, messageRecord, timestamp) => {
  const readAt = session.readReceipts?.[String(messageRecord.id)]
  const incomingMessage =
    messageRecord.isInterlocutorMessage && readAt
      ? messageRecord.merge({
          seenAt: messageRecord.seenAt || readAt,
          readStateKnown: true
        })
      : messageRecord
  const mergedMessages = mergeMessageGroups(session.messages || [], [
    [incomingMessage]
  ])
  return session.merge({
    messages: mergedMessages,
    lastActivityAt: timestamp,
    unreadCount: countUnreadMessages(mergedMessages)
  })
}

const replaceChatId = (list = [], previousChatId, chatId) =>
  sortDistinct(
    list.map(id => (String(id) === String(previousChatId) ? chatId : id))
  )

const markConversationSeenInList = (list = [], conversationId, readMessages) =>
  list.map(item => {
    if (
      String(item?.id) !== String(conversationId) ||
      !item.isInterlocutorMessage ||
      !readMessages.some(
        message =>
          message.text === item.text &&
          Date.parse(message.createdAt) === Date.parse(item.createdAt)
      )
    ) {
      return item
    }
    return item && typeof item.merge === 'function'
      ? item.merge({ messageSeen: true })
      : { ...item, messageSeen: true }
  })

const previewKey = item => {
  if (item?.id != null) return `conversation:${item.id}`
  if (item?.interlocutorId == null) return null
  return [
    item.messageType,
    item.interlocutorId,
    item.contextType,
    item.contextId
  ].join('|')
}

const mergeDistinctPreviews = (first = [], second = []) => {
  const seen = new Set()
  return [...first, ...second].filter(item => {
    const key = previewKey(item)
    if (!key) return true
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const updatePreviewList = (list, data, refresh, append) => {
  if (refresh) return mergeDistinctPreviews(data, list)
  if (append) return mergeDistinctPreviews(list, data)
  return data
}

const applyMessagesRead = (state, payload) => {
  const { conversationId, messageIds = [], seenAt } = payload || {}
  const chatId = state.conversationChatMap?.[conversationId]
  const session = state.chatSessions?.[chatId]
  if (!session || !seenAt || !messageIds.length) return state
  const ids = new Set(messageIds.map(String))
  const readMessages = session.messages.reduce(
    (all, group) =>
      all.concat(
        group.filter(
          message =>
            message.isInterlocutorMessage && ids.has(String(message.id))
        )
      ),
    []
  )
  const newlyRead = readMessages.filter(isUnreadIncomingMessage).length
  const nextState = updateChatSession(state, chatId, current =>
    current.merge({
      readReceipts: messageIds.reduce(
        (receipts, id) => ({
          ...receipts,
          [String(id)]: receipts[String(id)] || seenAt
        }),
        { ...current.readReceipts }
      ),
      messages: current.messages.map(group =>
        group.map(message =>
          message.isInterlocutorMessage &&
          ids.has(String(message.id)) &&
          !message.seenAt
            ? message.merge({ seenAt, readStateKnown: true })
            : message
        )
      ),
      unreadCount: Math.max(0, current.unreadCount - newlyRead),
      lastReadAt: Math.max(
        Number(current.lastReadAt || 0),
        Date.parse(seenAt) || 0
      )
    })
  )
  return syncActiveConversation(
    [
      'personalMessages',
      'contextualMessages',
      'personalSearchResults',
      'contextualSearchResults'
    ].reduce(
      (result, field) =>
        result.update(field, list =>
          markConversationSeenInList(list, conversationId, readMessages)
        ),
      nextState
    )
  )
}

export const messagesReducer = (state = new Messages(), action) => {
  switch (action.type) {
    case setMessagesListOpenedRoutine.SUCCESS:
      return state.set('listOpened', action.payload.opened)

    case CHAT_SESSION_UI_UPDATED: {
      const { chatId, draft, scrollPosition } = action.payload
      return syncActiveConversation(
        updateChatSession(state, chatId, session =>
          session.merge({
            ...(typeof draft === 'string' ? { draft } : {}),
            ...(scrollPosition !== undefined ? { scrollPosition } : {})
          })
        )
      )
    }

    case setConversationOpenedRoutine.SUCCESS: {
      const { chatId, conversationId } = action.payload
      const timestamp = nowTimestamp()

      const preparedState = state.update('chatSessions', sessions => ({
        ...sessions,
        [chatId]: sessions?.[chatId] || new Conversation({ chatId })
      }))
      let nextState = updateChatSession(preparedState, chatId, session =>
        session.merge({
          ...action.payload,
          chatId,
          status: CHAT_SESSION_STATUS.OPEN,
          minimizedAt: null,
          openedAt: session.openedAt || timestamp,
          lastActivityAt: timestamp
        })
      )

      nextState = nextState.update('chatOrder', order =>
        sortDistinct([chatId, ...(order || [])])
      )

      nextState = nextState.update('openChatIds', ids =>
        pushDistinctFront(ids || [], chatId)
      )

      nextState = nextState.update('minimizedChatIds', ids =>
        removeFromList(ids || [], chatId)
      )

      nextState = enforceDockLimits(nextState, timestamp)

      if (conversationId) {
        nextState = mapConversationToChat(nextState, conversationId, chatId)
      }

      nextState = nextState.set('activeChatId', chatId)

      return syncActiveConversation(nextState.set('conversationOpened', true))
    }

    case setChatSessionActiveRoutine.SUCCESS: {
      const { chatId } = action.payload
      const timestamp = nowTimestamp()

      let nextState = updateChatSession(state, chatId, session =>
        session.merge({
          status: CHAT_SESSION_STATUS.OPEN,
          minimizedAt: null,
          lastActivityAt: timestamp
        })
      )

      nextState = nextState.update('openChatIds', ids =>
        pushDistinctFront(ids || [], chatId)
      )

      nextState = nextState.update('minimizedChatIds', ids =>
        removeFromList(ids || [], chatId)
      )

      nextState = nextState.set('activeChatId', chatId)
      nextState = enforceDockLimits(nextState, timestamp)

      return syncActiveConversation(nextState)
    }

    case setChatSessionMinimizedRoutine.SUCCESS: {
      const { chatId } = action.payload
      const timestamp = nowTimestamp()

      let nextState = updateChatSession(state, chatId, session =>
        session.merge({
          status: CHAT_SESSION_STATUS.MINIMIZED,
          minimizedAt: timestamp,
          lastActivityAt: timestamp
        })
      )

      nextState = nextState.update('openChatIds', ids =>
        removeFromList(ids || [], chatId)
      )

      nextState = nextState.update('minimizedChatIds', ids =>
        pushDistinctFront(ids || [], chatId)
      )

      if (String(nextState.activeChatId) === String(chatId)) {
        nextState = nextState.set('activeChatId', null)
      }
      nextState = enforceDockLimits(nextState, timestamp)

      return syncActiveConversation(nextState)
    }

    case closeChatSessionRoutine.SUCCESS: {
      const { chatId } = action.payload
      const timestamp = nowTimestamp()

      let nextState = updateChatSession(state, chatId, session =>
        session.merge({
          status: CHAT_SESSION_STATUS.CLOSED,
          minimizedAt: null,
          lastActivityAt: timestamp
        })
      )

      nextState = nextState.update('openChatIds', ids =>
        removeFromList(ids || [], chatId)
      )

      nextState = nextState.update('minimizedChatIds', ids =>
        removeFromList(ids || [], chatId)
      )

      if (String(nextState.activeChatId) === String(chatId)) {
        nextState = nextState.set('activeChatId', null)
      }

      return syncActiveConversation(nextState)
    }

    case setChatDockConstraintsRoutine.SUCCESS: {
      const { maxOpenChats, maxDockChats } = action.payload || {}
      let nextState = state

      if (typeof maxOpenChats === 'number') {
        nextState = nextState.set('maxOpenChats', maxOpenChats)
      }

      if (typeof maxDockChats === 'number') {
        nextState = nextState.set('maxDockChats', maxDockChats)
      }

      nextState = enforceDockLimits(nextState, nowTimestamp())

      return syncActiveConversation(nextState)
    }

    case sendMessageRoutine.REQUEST: {
      const { chatId, message, retry } = action.payload
      return syncActiveConversation(
        updateChatSession(state, chatId, session =>
          session.merge({
            messages: mergeMessageGroups(
              session.messages.map(group =>
                group.filter(item => item.clientId !== message.clientId)
              ),
              [[message]]
            ),
            draft: !retry && session.draft === message.text ? '' : session.draft
          })
        )
      )
    }

    case sendMessageRoutine.FAILURE: {
      const { chatId, clientId } = action.payload
      const targetChatId = state.chatSessions?.[chatId]
        ? chatId
        : Object.keys(state.chatSessions).find(id =>
            state.chatSessions[id].messages.some(group =>
              group.some(message => message.clientId === clientId)
            )
          )
      return syncActiveConversation(
        updateChatSession(state, targetChatId, session =>
          session.set(
            'messages',
            session.messages.map(group =>
              group.map(message =>
                message.clientId === clientId && !hasServerMessageId(message)
                  ? message.set('sendStatus', 'failed')
                  : message
              )
            )
          )
        )
      )
    }

    case sendMessageRoutine.SUCCESS: {
      const { chatId, clientId, data, message } = action.payload
      const timestamp = nowTimestamp()

      const resolvedChatId =
        state.conversationChatMap?.[data.conversationId] ||
        (state.chatSessions?.[chatId] ? chatId : null)

      if (!state.chatSessions?.[resolvedChatId]) {
        return state
      }

      let nextState = updateChatSession(state, resolvedChatId, session =>
        appendMessageToSession(
          session.merge({
            conversationId: data.conversationId,
            messages: session.messages.map(group =>
              group.filter(item => item.clientId !== clientId)
            )
          }),
          message,
          timestamp
        )
      )

      nextState = mapConversationToChat(
        nextState,
        data.conversationId,
        resolvedChatId
      )

      return syncActiveConversation(nextState)
    }

    case bootstrapConversationRoutine.SUCCESS: {
      const { chatId, conversationId, previousChatId } = action.payload
      const targetChatId =
        chatId ||
        state.conversationChatMap?.[conversationId] ||
        state.activeChatId

      if (
        !state.chatSessions?.[targetChatId] &&
        !state.chatSessions?.[previousChatId]
      ) {
        return state
      }

      const preparedState = state.update('chatSessions', sessions => ({
        ...sessions,
        [targetChatId]:
          sessions[targetChatId] ||
          toConversationRecord(sessions[previousChatId])
      }))
      let nextState = updateChatSession(preparedState, targetChatId, session =>
        session.merge({
          ...action.payload,
          chatId: targetChatId,
          conversationId:
            action.payload.conversationId ?? session.conversationId
        })
      )

      if (conversationId) {
        nextState = mapConversationToChat(
          nextState,
          conversationId,
          targetChatId
        )
      }

      if (previousChatId && String(previousChatId) !== String(targetChatId)) {
        const previousSession = state.chatSessions?.[previousChatId]
        if (previousSession) {
          const targetSession = toConversationRecord(
            nextState.chatSessions?.[targetChatId]
          )
          const previous = toConversationRecord(previousSession)
          let mergedStatus = targetSession.status
          if (
            previous.status === CHAT_SESSION_STATUS.OPEN ||
            targetSession.status === CHAT_SESSION_STATUS.OPEN
          ) {
            mergedStatus = CHAT_SESSION_STATUS.OPEN
          } else if (previous.status === CHAT_SESSION_STATUS.MINIMIZED) {
            mergedStatus = CHAT_SESSION_STATUS.MINIMIZED
          }

          nextState = nextState
            .set(
              'chatSessions',
              Object.entries(nextState.chatSessions || {}).reduce(
                (sessions, [id, session]) =>
                  String(id) !== String(previousChatId) &&
                  String(id) !== String(targetChatId)
                    ? { ...sessions, [id]: session }
                    : sessions,
                {
                  [targetChatId]: targetSession.merge({
                    chatId: targetChatId,
                    status: mergedStatus,
                    draft: targetSession.draft || previous.draft,
                    scrollPosition:
                      targetSession.scrollPosition || previous.scrollPosition,
                    readReceipts: {
                      ...previous.readReceipts,
                      ...targetSession.readReceipts
                    },
                    messages: mergeMessageGroups(
                      targetSession.messages,
                      previous.messages
                    ),
                    openedAt: previous.openedAt || targetSession.openedAt,
                    lastActivityAt: Math.max(
                      Number(targetSession.lastActivityAt || 0),
                      Number(previous.lastActivityAt || 0)
                    ),
                    unreadCount: Math.max(
                      Number(targetSession.unreadCount || 0),
                      Number(previous.unreadCount || 0)
                    )
                  })
                }
              )
            )
            .update('openChatIds', ids =>
              replaceChatId(ids, previousChatId, targetChatId)
            )
            .update('minimizedChatIds', ids =>
              replaceChatId(ids, previousChatId, targetChatId)
            )
            .update('chatOrder', ids =>
              replaceChatId(ids, previousChatId, targetChatId)
            )
            .update('conversationChatMap', map =>
              Object.entries(map || {}).reduce(
                (result, [id, mappedChatId]) => ({
                  ...result,
                  [id]:
                    String(mappedChatId) === String(previousChatId)
                      ? targetChatId
                      : mappedChatId
                }),
                {}
              )
            )

          if (mergedStatus === CHAT_SESSION_STATUS.OPEN) {
            nextState = nextState
              .update('openChatIds', ids =>
                pushDistinctFront(ids || [], targetChatId)
              )
              .update('minimizedChatIds', ids =>
                removeFromList(ids || [], targetChatId)
              )
          } else if (mergedStatus === CHAT_SESSION_STATUS.MINIMIZED) {
            nextState = nextState
              .update('openChatIds', ids =>
                removeFromList(ids || [], targetChatId)
              )
              .update('minimizedChatIds', ids =>
                pushDistinctFront(ids || [], targetChatId)
              )
          } else {
            nextState = nextState
              .update('openChatIds', ids =>
                removeFromList(ids || [], targetChatId)
              )
              .update('minimizedChatIds', ids =>
                removeFromList(ids || [], targetChatId)
              )
          }

          if (String(nextState.activeChatId) === String(previousChatId)) {
            nextState = nextState.set('activeChatId', targetChatId)
          }
        }
      }

      return syncActiveConversation(nextState)
    }

    case getConversationMessagesRoutine.REQUEST: {
      if (action.payload.refresh) return state
      const { chatId, conversationId } = action.payload
      const targetChatId =
        state.conversationChatMap?.[conversationId] ||
        chatId ||
        state.activeChatId

      if (!state.chatSessions?.[targetChatId]) {
        return state
      }

      const nextState = updateChatSession(state, targetChatId, session =>
        session.merge({ messagesState: API_STATES.IN_PROGRESS })
      )

      return syncActiveConversation(
        nextState.set('conversationState', API_STATES.IN_PROGRESS)
      )
    }

    case getConversationMessagesRoutine.SUCCESS: {
      const { chatId, conversationId } = action.payload
      const targetChatId =
        state.conversationChatMap?.[conversationId] ||
        chatId ||
        state.activeChatId

      if (!state.chatSessions?.[targetChatId]) {
        return state
      }

      const { data: incomingGroups = [] } = action.payload

      let nextState = updateChatSession(state, targetChatId, session => {
        const messages = mergeMessageGroups(
          session.messages || [],
          incomingGroups
        ).map(group =>
          group.map(message => {
            const seenAt = session.readReceipts?.[String(message.id)]
            return message.isInterlocutorMessage && seenAt && !message.seenAt
              ? message.merge({ seenAt, readStateKnown: true })
              : message
          })
        )
        return session.merge({
          messagesState: action.payload.refresh
            ? session.messagesState
            : API_STATES.DONE,
          messages,
          unreadCount: countUnreadMessages(messages)
        })
      })

      nextState = mapConversationToChat(nextState, conversationId, targetChatId)
      if (!action.payload.refresh) {
        nextState = nextState.setIn(
          ['lastRequest', conversationId],
          action.payload.data
        )
      }

      return syncActiveConversation(nextState)
    }

    case getConversationMessagesRoutine.FAILURE: {
      if (action.payload?.refresh) return state
      const chatId =
        state.conversationChatMap?.[action.payload?.conversationId] ||
        action.payload?.chatId ||
        state.activeChatId
      const nextState = updateChatSession(state, chatId, session =>
        session.merge({ messagesState: API_STATES.DONE })
      )
      return syncActiveConversation(nextState)
    }

    case getLastContextualMessagesListRoutine.REQUEST:
      return state.set('listState', API_STATES.IN_PROGRESS)

    case getLastContextualMessagesListRoutine.SUCCESS: {
      const { data, append, refresh, meta } = action.payload
      const preservePagination = refresh && state.contextualMessages.length > 0
      const nextPage = Math.min(meta.currentPage + 1, meta.lastPage)
      return state
        .update('contextualMessages', list =>
          updatePreviewList(list, data, refresh, append)
        )
        .set(
          'contextualMessagesPage',
          preservePagination ? state.contextualMessagesPage : nextPage
        )
        .set(
          'contextualMessagesAllFetched',
          preservePagination
            ? state.contextualMessagesAllFetched
            : meta.currentPage === meta.lastPage
        )
        .set('listState', API_STATES.DONE)
    }

    case getLastPersonalMessagesListRoutine.REQUEST:
      return state.set('listState', API_STATES.IN_PROGRESS)

    case getLastPersonalMessagesListRoutine.SUCCESS: {
      const { data, append, refresh, meta } = action.payload
      const preservePagination = refresh && state.personalMessages.length > 0
      const nextPage = Math.min(meta.currentPage + 1, meta.lastPage)
      return state
        .update('personalMessages', list =>
          updatePreviewList(list, data, refresh, append)
        )
        .set(
          'personalMessagesPage',
          preservePagination ? state.personalMessagesPage : nextPage
        )
        .set(
          'personalMessagesAllFetched',
          preservePagination
            ? state.personalMessagesAllFetched
            : meta.currentPage === meta.lastPage
        )
        .set('listState', API_STATES.DONE)
    }

    case getLastPersonalMessagesListRoutine.FULFILL:
      return state.set('listState', API_STATES.DONE)

    case getLastContextualMessagesListRoutine.FULFILL:
      return state.set('listState', API_STATES.DONE)

    case MESSAGE_EVENT_USER_MESSAGE_SENT:
    case MESSAGE_EVENT_SYSTEM_MESSAGE_SENT: {
      let nextState =
        action.type === MESSAGE_EVENT_SYSTEM_MESSAGE_SENT
          ? state.update('offerRevision', revision => revision + 1)
          : state

      const chatId =
        nextState.conversationChatMap?.[action.payload.conversation_id]
      if (!chatId) {
        return nextState
      }

      const existingSession =
        nextState.chatSessions?.[chatId] instanceof Conversation
          ? nextState.chatSessions[chatId]
          : new Conversation(nextState.chatSessions?.[chatId])

      const wasOpen = existingSession.status === CHAT_SESSION_STATUS.OPEN

      const timestamp = nowTimestamp()

      nextState = updateChatSession(nextState, chatId, session =>
        appendMessageToSession(
          session,
          normalizeReceivedMessage(action.payload),
          timestamp
        )
      )

      if (action.type === MESSAGE_EVENT_USER_MESSAGE_SENT) {
        if (wasOpen) {
          nextState = nextState.update('openChatIds', ids =>
            pushDistinctFront(ids || [], chatId)
          )
        }
      }

      return syncActiveConversation(nextState)
    }

    case getConversationContextRoutine.REQUEST: {
      const chatId = action.payload?.chatId || state.activeChatId
      const nextState = updateChatSession(state, chatId, session =>
        session.merge({ contextState: API_STATES.IN_PROGRESS })
      )
      return syncActiveConversation(nextState)
    }

    case getConversationContextRoutine.SUCCESS: {
      const chatId = action.payload.chatId || action?.meta?.chatId
      const nextState = updateChatSession(state, chatId, session =>
        session
          .merge({ contextState: API_STATES.DONE })
          .set('context', action.payload.data)
      )
      return syncActiveConversation(nextState)
    }

    case getConversationContextRoutine.FAILURE: {
      const chatId = action.payload?.chatId || state.activeChatId
      const nextState = updateChatSession(state, chatId, session =>
        session.merge({ contextState: API_STATES.DONE })
      )
      return syncActiveConversation(nextState)
    }

    case getUnreadMessagesCountRoutine.SUCCESS:
      return state
        .set('allUnreadMessagesCount', action.payload.all)
        .set('contextualMessagesCount', action.payload.contextual)
        .set('personalMessagesCount', action.payload.personal)

    case markMessageAsSeenRoutine.SUCCESS:
      return applyMessagesRead(state, action.payload)

    case searchForUserConversationsRoutine.TRIGGER:
      return state
        .set('personalSearchResultsAllFetched', false)
        .set('contextualSearchResultsAllFetched', false)

    case searchForUserConversationsRoutine.REQUEST:
      return state.set('listState', API_STATES.IN_PROGRESS)

    case searchForUserConversationsRoutine.SUCCESS:
      return state
        .update(action.payload.field, current =>
          action.payload.append
            ? current.concat(action.payload.data)
            : action.payload.data
        )
        .set(
          action.payload.pageValueName,
          action.payload.meta.currentPage + 1 > action.payload.meta.lastPage
            ? action.payload.meta.lastPage
            : action.payload.meta.currentPage + 1
        )
        .set(
          action.payload.allFetchedValueName,
          action.payload.meta.currentPage === action.payload.meta.lastPage
        )
        .set('listState', API_STATES.DONE)

    case searchForUserConversationsRoutine.FAILURE:
      return state.set('listState', API_STATES.DONE)

    case toggleConversationNotificationsRoutine.SUCCESS: {
      const { chatId, value } =
        typeof action.payload === 'object'
          ? action.payload
          : { chatId: state.activeChatId, value: action.payload }

      const nextState = updateChatSession(state, chatId, session =>
        session.merge({ isConversationMuted: value })
      )

      return syncActiveConversation(nextState)
    }

    case clearConversationRoutine.SUCCESS:
      return syncActiveConversation(
        state
          .set('chatSessions', {})
          .set('chatOrder', [])
          .set('openChatIds', [])
          .set('minimizedChatIds', [])
          .set('conversationChatMap', {})
          .set('lastRequest', {})
          .set('conversation', new Conversation())
          .set('conversationOpened', false)
      )

    case CHAT_PRESENCE_CHANNEL_INIT: {
      const userIds = action.payload.users.reduce((acc, val) => {
        acc[val.id] = true
        return acc
      }, {})
      return state.set('onlineUsers', userIds)
    }

    case CHAT_PRESENCE_CHANNEL_JOIN:
      return state.update('onlineUsers', users =>
        assoc(action.payload.user.id, true, users)
      )

    case CHAT_PRESENCE_CHANNEL_LEAVE:
      return state.update('onlineUsers', users =>
        dissoc(action.payload.user.id, users)
      )

    case getContextAvailableActionsRoutine.REQUEST: {
      const chatId = action.payload?.chatId || state.activeChatId
      const nextState = updateChatSession(state, chatId, session =>
        session.merge({ contextState: API_STATES.IN_PROGRESS })
      )
      return syncActiveConversation(nextState)
    }

    case getContextAvailableActionsRoutine.SUCCESS: {
      const { chatId } = action.payload
      const targetChatId = chatId || state.activeChatId
      const nextState = updateChatSession(state, targetChatId, session =>
        session
          .merge({ contextState: API_STATES.DONE })
          .set('contextActions', action.payload)
      )
      return syncActiveConversation(nextState)
    }

    case getContextAvailableActionsRoutine.FAILURE: {
      const chatId = action.payload?.chatId || state.activeChatId
      const nextState = updateChatSession(state, chatId, session =>
        session.merge({ contextState: API_STATES.DONE })
      )
      return syncActiveConversation(nextState)
    }

    case clearLastMessagesListsRoutine.SUCCESS:
      return state
        .set('personalMessagesPage', 1)
        .set('contextualMessagesPage', 1)
        .set('personalSearchResultsPage', 1)
        .set('contextualSearchResultsPage', 1)
        .set('personalMessagesAllFetched', false)
        .set('contextualMessagesAllFetched', false)
        .set('personalSearchResultsAllFetched', false)
        .set('contextualSearchResultsAllFetched', false)

    case CHAT_OTHER_TAB_MESSAGE_SENT: {
      const {
        conversationId,
        receiverId,
        contextId,
        contextType
      } = action.payload
      const resolvedChatId =
        state.conversationChatMap?.[conversationId] ||
        Object.keys(state.chatSessions).find(id => {
          const session = state.chatSessions[id]
          return (
            receiverId != null &&
            String(session.receiverId) === String(receiverId) &&
            String(session.contextId ?? null) === String(contextId ?? null) &&
            String(session.contextType ?? null) === String(contextType ?? null)
          )
        })
      if (!resolvedChatId) {
        return state
      }

      let nextState = updateChatSession(state, resolvedChatId, session =>
        appendMessageToSession(
          session.set('conversationId', conversationId),
          action.payload.message,
          nowTimestamp()
        )
      )

      if (conversationId) {
        nextState = mapConversationToChat(
          nextState,
          conversationId,
          resolvedChatId
        )
      }

      return syncActiveConversation(nextState)
    }

    case CHAT_CONVERSATION_MESSAGES_READ:
      return applyMessagesRead(state, action.payload)

    default:
      return state
  }
}
